import { useEffect, useState, type ReactNode } from 'react';
import { AlertTriangle, ChevronDown, ChevronRight, ExternalLink, Loader2, X } from 'lucide-react';
import { analystApi } from '../../services/api';
import type { CitationView, DatasetRowView, RenderedReport, StoryView } from '../../shared/analyst/api';
import type { DataAnswer, DraftReport } from '../../shared/analyst/run';
import { ReportChart } from './Charts';
import { RefText, SignalBadge } from './RefText';

/** Report strutturato (agentanalyst.md §11, §13.1): verdetto, evidenze, sezioni, grafici. */

function isDraft(body: Record<string, unknown>): body is Record<string, unknown> & DraftReport {
  return typeof body['verdict'] === 'object' && body['verdict'] !== null && Array.isArray(body['evidence']);
}

function isAnswer(body: Record<string, unknown>): body is Record<string, unknown> & DataAnswer {
  return typeof body['answer'] === 'string';
}

interface LimitsBody { reason: string; details: string[]; fixes: string[] }

function isLimits(body: Record<string, unknown>): body is Record<string, unknown> & LimitsBody {
  return Array.isArray(body['details']) && Array.isArray(body['fixes']);
}

function Section({ title, children, open: initial = true }: { title: string; children: ReactNode; open?: boolean }) {
  const [open, setOpen] = useState(initial);
  return (
    <section className="border-t border-gray-800 pt-3">
      <button type="button" onClick={() => setOpen(!open)} className="flex items-center gap-1 text-xs font-semibold tracking-wide text-gray-400 uppercase hover:text-gray-200">
        {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
        {title}
      </button>
      {open && <div className="mt-2 text-sm text-gray-200 leading-relaxed space-y-2">{children}</div>}
    </section>
  );
}

export function CitationPanel({ citation, datasetId, onClose }: { citation: CitationView; datasetId: string | null; onClose: () => void }) {
  const [row, setRow] = useState<DatasetRowView | null>(null);
  const [story, setStory] = useState<StoryView | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!datasetId) return;
    setRow(null);
    setStory(null);
    setError(null);
    analystApi.datasetRows(datasetId, { pulsar: citation.pulsar_id, limit: '1' })
      .then(async (r) => {
        if (cancelled) return;
        const found = r.rows[0] ?? null;
        setRow(found);
        if (found && found.story_size > 1) {
          const s = await analystApi.story(datasetId, found.story_id);
          if (!cancelled) setStory(s);
        }
      })
      .catch((err: unknown) => { if (!cancelled) setError(err instanceof Error ? err.message : String(err)); });
    return () => { cancelled = true; };
  }, [citation.pulsar_id, datasetId]);

  const n = citation.notes;
  return (
    <aside className="fixed inset-y-0 right-0 w-full max-w-md bg-gray-900 border-l border-gray-700 shadow-2xl z-50 overflow-y-auto">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-800">
        <h3 className="text-sm font-semibold text-white">Citazione {citation.citation_id}</h3>
        <button type="button" onClick={onClose} className="text-gray-400 hover:text-white" aria-label="Chiudi"><X className="w-4 h-4" /></button>
      </div>
      <div className="p-4 space-y-4 text-sm">
        <blockquote className="border-l-2 border-amber-500 pl-3 text-amber-100">«{citation.excerpt}»</blockquote>
        <dl className="grid grid-cols-3 gap-x-2 gap-y-1 text-xs">
          <dt className="text-gray-500">Autore</dt><dd className="col-span-2 text-gray-200">{n.kind === 'article' ? n.domain ?? '—' : `@${n.author ?? '?'}`}</dd>
          <dt className="text-gray-500">Fonte</dt><dd className="col-span-2 text-gray-200">{n.source ?? '—'}</dd>
          <dt className="text-gray-500">Data (UTC)</dt><dd className="col-span-2 text-gray-200">{new Date(n.date_utc).toISOString().replace('T', ' ').slice(0, 16)}</dd>
          <dt className="text-gray-500">Voce</dt><dd className="col-span-2 text-gray-200">{n.voice_type}</dd>
          {n.quoted_author && <><dt className="text-gray-500">Cita</dt><dd className="col-span-2 text-gray-200">@{n.quoted_author} (testo proprio separato)</dd></>}
        </dl>
        <div className="flex flex-wrap gap-1.5">
          {n.publisher_on_social && <span className="text-xs bg-orange-900/40 border border-orange-700 text-orange-200 rounded px-2 py-0.5">editore sui social: non è una voce organica</span>}
          {n.reply_without_parent && <span className="text-xs bg-orange-900/40 border border-orange-700 text-orange-200 rounded px-2 py-0.5">risposta senza post padre nei dati</span>}
          {n.story_wire && <span className="text-xs bg-sky-900/40 border border-sky-700 text-sky-200 rounded px-2 py-0.5">lancio {n.story_wire}: testo dell'agenzia</span>}
        </div>
        {n.url && (
          <a href={n.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-blue-300 hover:text-blue-200">
            Apri l'originale <ExternalLink className="w-3 h-3" />
          </a>
        )}
        {!datasetId && <p className="text-xs text-gray-500">Dataset non disponibile.</p>}
        {error && <p className="text-xs text-red-300">{error}</p>}
        {datasetId && !row && !error && <p className="text-xs text-gray-500 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Caricamento della riga…</p>}
        {row && (
          <div className="space-y-2">
            <p className="text-xs font-semibold text-gray-400 uppercase">Riga completa</p>
            {row.title && <p className="text-gray-300 font-medium">{row.title}</p>}
            <p className="text-gray-300 whitespace-pre-wrap text-xs bg-gray-800/60 rounded p-2 max-h-64 overflow-y-auto">{row.text ?? '(nessun testo)'}</p>
            <p className="text-xs text-gray-500">Sentiment Pulsar: {row.sentiment_class} · storia {row.story_id} ({row.story_size} righe)</p>
          </div>
        )}
        {story && (
          <div className="space-y-1">
            <p className="text-xs font-semibold text-gray-400 uppercase">Storia {story.storyId}</p>
            <p className="text-xs text-gray-300">
              {story.rows} righe{story.wire ? `, lancio ${story.wire}` : ''}, {story.outlets.length} testate e {story.accounts.length} account,
              dal {story.firstSeen.slice(0, 16).replace('T', ' ')} al {story.lastSeen.slice(0, 16).replace('T', ' ')} UTC.
            </p>
            {story.outlets.length > 0 && <p className="text-xs text-gray-500">Testate: {story.outlets.slice(0, 20).join(', ')}{story.outlets.length > 20 ? '…' : ''}</p>}
          </div>
        )}
      </div>
    </aside>
  );
}

export function ReportView({ report, datasetId }: { report: RenderedReport; datasetId: string | null }) {
  const [citation, setCitation] = useState<CitationView | null>(null);
  const [showMeta, setShowMeta] = useState(false);
  const body = report.body;
  const ref = (text: string) => <RefText text={text} report={report} onCitation={setCitation} />;

  return (
    <div className="space-y-3">
      {report.verificationIssues.length > 0 && (
        <div className="flex gap-2 bg-amber-900/30 border border-amber-700 rounded-lg px-3 py-2 text-xs text-amber-100">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          <div>
            <p className="font-semibold">Avvisi di verifica non risolti</p>
            <ul className="list-disc ml-4">{report.verificationIssues.map((i, k) => <li key={k}>{i.message}</li>)}</ul>
          </div>
        </div>
      )}
      {report.materialFlags.length > 0 && report.kind !== 'limits' && (
        <details open className="bg-amber-950/30 border border-amber-800/60 rounded-lg px-3 py-2 text-xs text-gray-200">
          <summary className="cursor-pointer font-semibold text-amber-100">Limiti dei dati: {report.materialFlags.length} avvertenze che incidono sulla lettura</summary>
          <ul className="list-disc ml-4 mt-1 space-y-0.5">{report.materialFlags.map((f) => <li key={f.code}><span className="text-gray-400">{f.code}</span>: {f.message}</li>)}</ul>
        </details>
      )}

      {isDraft(body) && (
        <>
          <div className="bg-gray-800/70 border border-gray-700 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2"><span className="text-xs font-semibold text-gray-400 uppercase">Verdetto</span><SignalBadge signal={body.verdict.signal} withMeaning /></div>
            <p className="text-base text-white leading-relaxed">{ref(body.verdict.text)}</p>
            <p className="text-xs text-gray-400 mt-2">Segnale {body.verdict.signal}: {ref(body.verdict.signal_reason)}</p>
          </div>
          <Section title="Evidence">
            <ul className="space-y-2">{body.evidence.map((e, i) => (
              <li key={i}>{ref(e.text)}{e.baseline && <span className="block text-xs text-gray-500 mt-0.5">Baseline: {ref(e.baseline)}</span>}</li>
            ))}</ul>
            <p className="text-xs text-gray-500">Baseline del report: {ref(body.baseline_used)}</p>
          </Section>
          {body.top_3 && body.top_3.length > 0 && (
            <Section title="Top 3">
              <div className="grid gap-2 md:grid-cols-3">{body.top_3.map((t, i) => (
                <div key={i} className="bg-gray-800/50 border border-gray-700 rounded-lg p-3 text-xs">{ref(t.text)}</div>
              ))}</div>
            </Section>
          )}
          {body.closer_look && <Section title="Closer look" open={false}>{ref(body.closer_look)}</Section>}
          {body.so_what && <Section title="So what" open={false}>{ref(body.so_what)}</Section>}
          {body.interpretations && body.interpretations.length > 0 && (
            <Section title="Interpretazioni e implicazioni">
              <p className="text-xs text-gray-500">Letture e indicazioni che derivano dalle evidenze sopra: ognuna dichiara su quali poggia.</p>
              <ul className="space-y-3">{body.interpretations.map((x, i) => (
                <li key={i} className="border-l-2 border-indigo-700 pl-3">
                  <span className="text-xs font-semibold uppercase text-indigo-300">{x.kind === 'implicazione' ? 'Implicazione' : 'Interpretazione'}</span>
                  <p>{ref(x.text)}</p>
                  <p className="text-xs text-gray-400 mt-1">Basata su: {ref(x.premises)}</p>
                </li>
              ))}</ul>
            </Section>
          )}
          {body.requirements_coverage && body.requirements_coverage.length > 0 && (
            <Section title="Copertura della domanda" open={false}>
              <ul className="space-y-1">{body.requirements_coverage.map((c) => (
                <li key={c.requirement_id}>
                  <span className={c.status === 'misurato' ? 'text-emerald-300' : c.status === 'interpretato' ? 'text-indigo-300' : 'text-amber-300'}>
                    {c.status === 'misurato' ? 'Misurato' : c.status === 'interpretato' ? 'Interpretato su evidenze' : 'Non rispondibile'}
                  </span>
                  {' — '}<span className="text-gray-300">{report.requirements.find((q) => q.id === c.requirement_id)?.text ?? c.requirement_id}</span>
                  <span className="block text-xs text-gray-400">{ref(c.where)}{c.reason && <> ({ref(c.reason)})</>}</span>
                </li>
              ))}</ul>
            </Section>
          )}
          {body.caveats.length > 0 && (
            <Section title="Caveats" open={false}>
              <ul className="list-disc ml-4 space-y-1">{body.caveats.map((c, i) => <li key={i}>{ref(c.text)}</li>)}</ul>
            </Section>
          )}
        </>
      )}

      {isAnswer(body) && (
        <div className="bg-gray-800/70 border border-gray-700 rounded-lg p-4 text-white">
          <p>{ref(body.answer)}</p>
          {body.caveat && <p className="text-xs text-gray-400 mt-2">{ref(body.caveat)}</p>}
        </div>
      )}

      {isLimits(body) && (
        <div className="bg-red-900/20 border border-red-800 rounded-lg p-4 text-sm text-red-100 space-y-2">
          <div className="flex items-center gap-2"><SignalBadge signal="INSUFFICIENT" /><span className="font-semibold">Nessuna analisi quantitativa possibile</span></div>
          <ul className="list-disc ml-4">{body.details.map((d, i) => <li key={i}>{d}</li>)}</ul>
          <p className="text-xs font-semibold text-red-200 uppercase">Cosa servirebbe</p>
          <ul className="list-disc ml-4">{body.fixes.map((d, i) => <li key={i}>{d}</li>)}</ul>
        </div>
      )}

      {report.charts.length > 0 && (
        <Section title="Grafici" open={false}>
          <div className="space-y-3">{report.charts.map((c) => <ReportChart key={c.type} chart={c} />)}</div>
        </Section>
      )}

      {report.metadata && (
        <div>
          <button type="button" onClick={() => setShowMeta(!showMeta)} className="text-xs text-gray-500 hover:text-gray-300">
            {showMeta ? 'Nascondi' : 'Mostra'} dettagli tecnici
          </button>
          {showMeta && <pre className="mt-1 text-[11px] text-gray-400 bg-gray-950 border border-gray-800 rounded p-2 overflow-x-auto">{JSON.stringify(report.metadata, null, 2)}</pre>}
        </div>
      )}

      {citation && <CitationPanel citation={citation} datasetId={datasetId} onClose={() => setCitation(null)} />}
    </div>
  );
}
